"use client";

import { Html } from "@/Components";
import { CenterOptions } from "@/Helpers/CenterOptions";
import { getSectionDefaultProps } from "@/Templates/Doccure/getSectionDefaultProps";
import { BannerSectionProps } from "@/Templates/SectionsProps";
import {
  cloneElement,
  FC,
  isValidElement,
  ReactElement,
  ReactNode,
} from "react";
import { SearchBox } from "../Components";
import HomeEightBannerSectionGenerated, {
  HomeEightBannerSectionGeneratedProps,
} from "../Sections/_generated/HomeEightBannerSection.generated";
import HomeFiveBannerSectionGenerated, {
  HomeFiveBannerSectionGeneratedProps,
} from "../Sections/_generated/HomeFiveBannerSection.generated";
import HomeFourBannerSectionGenerated, {
  HomeFourBannerSectionGeneratedProps,
} from "../Sections/_generated/HomeFourBannerSection.generated";
import HomeFourteenBannerSectionGenerated, {
  HomeFourteenBannerSectionGeneratedProps,
} from "../Sections/_generated/HomeFourteenBannerSection.generated";
import HomeNineBannerSectionGenerated, {
  HomeNineBannerSectionGeneratedProps,
} from "../Sections/_generated/HomeNineBannerSection.generated";
import HomeOneBannerGenerated, {
  HomeOneBannerGeneratedProps,
} from "../Sections/_generated/HomeOneBanner.generated";
import HomeSevenBannerSectionGenerated, {
  HomeSevenBannerSectionGeneratedProps,
} from "../Sections/_generated/HomeSevenBannerSection.generated";
import HomeSixBannerSectionGenerated, {
  HomeSixBannerSectionGeneratedProps,
} from "../Sections/_generated/HomeSixBannerSection.generated";
import HomeTenBannerSectionGenerated, {
  HomeTenBannerSectionGeneratedProps,
} from "../Sections/_generated/HomeTenBannerSection.generated";
import HomeThirteenBannerSectionGenerated, {
  HomeThirteenBannerSectionGeneratedProps,
} from "../Sections/_generated/HomeThirteenBannerSection.generated";
import HomeThreeBannerSectionGenerated, {
  HomeThreeBannerSectionGeneratedProps,
} from "../Sections/_generated/HomeThreeBannerSection.generated";
import HomeTwelveBannerSectionGenerated, {
  HomeTwelveBannerSectionGeneratedProps,
} from "../Sections/_generated/HomeTwelveBannerSection.generated";
import HomeTwoBannerSectionGenerated, {
  HomeTwoBannerSectionGeneratedProps,
} from "../Sections/_generated/HomeTwoBannerSection.generated";
import { renderGeneratedSection } from "./generated-section-renderer";
import JSONPretty from "react-json-pretty";

type GeneratedBannerRenderer = (props: BannerSectionProps) => ReactElement;
type GeneratedBannerProps = Record<string, unknown>;
type GeneratedBannerItem = Record<string, unknown>;

const BannerAppearance =
  CenterOptions.SiteTemplate.SiteTemplateSettings.SiteTemplateBannerAppearance;

function buildGeneratedBannerProps<T extends Record<string, unknown>>(
  sectionKey: string,
  overrides?: Partial<T>,
): T {
  return getSectionDefaultProps<T>(sectionKey, {
    ...(overrides ?? {}),
  } as Partial<T>);
}

function renderHtmlValue(value?: string | null): ReactNode {
  if (!value) {
    return null;
  }

  return <Html value={value} />;
}

function cloneNodeWithChildren(
  defaultNode: ReactNode,
  children: ReactNode,
): ReactNode {
  if (!isValidElement(defaultNode)) {
    return children;
  }

  return cloneElement(defaultNode, undefined, children);
}

function createNodeChildrenRenderer(value?: string | null) {
  if (!value) {
    return undefined;
  }

  return (defaultNode: ReactNode) =>
    cloneNodeWithChildren(defaultNode, renderHtmlValue(value));
}

function createIconLinkRenderer(
  iconClass?: string | null,
  text?: ReactNode,
) {
  if (!iconClass) {
    return undefined;
  }

  return (defaultNode: ReactNode) => {
    if (!isValidElement(defaultNode)) {
      return defaultNode;
    }

    return cloneElement(
      defaultNode,
      undefined,
      <i className={`${iconClass} me-2`} />,
      text,
    );
  };
}

function getBannerImageUrl(props: BannerSectionProps): string | undefined {
  return props.setting.Image?.Url ?? undefined;
}

function getBannerListImageUrls(props: BannerSectionProps): string[] {
  return props.lists.Default.map((item) => item.Image?.Url).filter(
    (url): url is string => Boolean(url),
  );
}

function applyCollectionImages(
  items: GeneratedBannerItem[],
  imageUrls: string[],
): GeneratedBannerItem[] {
  if (
    imageUrls.length === 0 ||
    items.length === 0 ||
    typeof items[0]?.src !== "string"
  ) {
    return items;
  }

  return items.map((item, index) => ({
    ...item,
    src: imageUrls[index % imageUrls.length] ?? item.src,
  }));
}

function applyBannerListItemImages<T extends GeneratedBannerProps>(
  generatedProps: T,
  props: BannerSectionProps,
): T {
  const imageUrls = getBannerListImageUrls(props);
  if (imageUrls.length === 0) {
    return generatedProps;
  }

  const nextProps: GeneratedBannerProps = {
    ...generatedProps,
  };

  for (const key of Object.keys(nextProps)) {
    const value = nextProps[key];
    if (
      !Array.isArray(value) ||
      value.length === 0 ||
      typeof value[0] !== "object" ||
      value[0] === null
    ) {
      continue;
    }

    nextProps[key] = applyCollectionImages(
      value as GeneratedBannerItem[],
      imageUrls,
    );
  }

  return nextProps as T;
}

function buildAnimatedButtonText(value?: string | null): string | undefined {
  if (!value) {
    return undefined;
  }

  return `${value} . ${value}`;
}

function applyHomeFourteenSlideOverrides(
  generatedProps: HomeFourteenBannerSectionGeneratedProps,
  props: BannerSectionProps,
): HomeFourteenBannerSectionGeneratedProps {
  const nextProps = applyBannerListItemImages(generatedProps, props);

  return {
    ...nextProps,
    bannerSectionBannerSlides: nextProps.bannerSectionBannerSlides.map(
      (item) => ({
        ...item,
        href: props.setting.ButtonLink ?? item.href,
        link0Text: props.setting.ButtonText ?? item.link0Text,
        p0Text: props.setting.Subtitle ?? item.p0Text,
      }),
    ),
    bannerSectionBannerSlidesLinkVisible:
      props.setting.ButtonDisplayStatus.Key ===
      CenterOptions.Enum.Status.Active.key,
  };
}

function applyHomeFiveSlideOverrides(
  generatedProps: HomeFiveBannerSectionGeneratedProps,
  props: BannerSectionProps,
): HomeFiveBannerSectionGeneratedProps {
  const imageUrls = getBannerListImageUrls(props);
  if (imageUrls.length === 0) {
    return generatedProps;
  }

  return {
    ...generatedProps,
    bannerSectionBannerSlides: generatedProps.bannerSectionBannerSlides.map(
      (item, index) => ({
        ...item,
        src: imageUrls[index % imageUrls.length] ?? item.src,
      }),
    ),
  };
}

const generatedBannerRenderers: Record<string, GeneratedBannerRenderer> = {
  [BannerAppearance.Home1.key]: (props) => (
    <HomeOneBannerGenerated
      {...buildGeneratedBannerProps<HomeOneBannerGeneratedProps>(
        "HomeOneBanner",
      )}
      sectionDiv0Div0Div0Div0H10H1Renderer={createNodeChildrenRenderer(
        props.setting.Title,
      )}
      sectionDiv0Div0Div1Div0ImageWithBasePath0ImageSrc={
        getBannerImageUrl(props) ??
        "/Templates/Doccure/img/banner/banner-01.png"
      }
      sectionDiv0Div0Div0Div0Div1Form0FormVisible={
        props.setting.SearchDisplayStatus.Key ===
        CenterOptions.Enum.Status.Active.key
      }
      sectionDiv0Div0Div0Div0Div1Form0Div1Div0Input0InputPlaceholder={
        props.setting.SearchPlaceholder ?? undefined
      }
      sectionDiv0Div0Div0Div0Div1Form0Div2Button0ButtonText={
        props.setting.ButtonText ?? undefined
      }
      sectionDiv0Div0Div0Div0Div1Form0FormRenderer={(_) => (
        <SearchBox
          searchPlaceholder={props.setting.SearchPlaceholder}
          submitText={props.setting.ButtonText}
        />
      )}
      sectionDiv0Div0Div0Div0Div0Div1P0PText={
        props.setting.StatisticText1 ?? undefined
      }
      sectionDiv0Div0Div0Div0Div0Div1Div0P0PText="5.0 امتیاز"
    />
  ),
  [BannerAppearance.Home2.key]: (props) => (
    <HomeTwoBannerSectionGenerated
      {...buildGeneratedBannerProps<HomeTwoBannerSectionGeneratedProps>(
        "HomeTwoBannerSection",
        {
          sectionDiv0Div0Div0Div0H10H1Renderer: createNodeChildrenRenderer(
            props.setting.Title,
          ),
          sectionDiv0Div0Div0Div0P0PText: props.setting.Subtitle ?? undefined,
          sectionDiv0Div0Div0Div0Div0Span0SpanText:
            props.setting.BadgeText ?? undefined,
          sectionDiv0Div0Div0Div0Div0P0PText:
            props.setting.BadgeDescription ?? undefined,
          sectionDiv0Div0Div0Div0Link0LinkHref:
            props.setting.ButtonLink ?? "all_routes.booking",
          sectionDiv0Div0Div0Div0Link0LinkText:
            props.setting.ButtonText ?? undefined,
          sectionDiv0Div0Div0Div0Link0LinkRenderer: createIconLinkRenderer(
            props.setting.ButtonIcon,
            props.setting.ButtonText ?? undefined,
          ),
          sectionDiv0Div0Div0Div0Link0LinkVisible:
            props.setting.ButtonDisplayStatus.Key ===
            CenterOptions.Enum.Status.Active.key,
          sectionDiv0Div0Div1Div0Div0P0PText:
            props.setting.CallingText ?? undefined,
          sectionDiv0Div0Div1Div0Div0Span0ImageWithBasePath0ImageSrc:
            props.setting.PatientImage?.Url ?? undefined,
          sectionDiv0Div0Div1Div0Div1Link0LinkVisible:
            props.setting.CallActionDisplayStatus.Key ===
            CenterOptions.Enum.Status.Active.key,
          sectionDiv0Div0Div1Div0Div1Link1LinkVisible:
            props.setting.CallActionDisplayStatus.Key ===
            CenterOptions.Enum.Status.Active.key,
          sectionDiv0Div0Div1Div0Div1Link2LinkVisible:
            props.setting.CallActionDisplayStatus.Key ===
            CenterOptions.Enum.Status.Active.key,
          sectionDiv0Div0Div1Div0ImageWithBasePath0ImageSrc:
            getBannerImageUrl(props) ?? undefined,
        },
      )}
    />
  ),
  [BannerAppearance.Home3.key]: (props) => (
    <HomeThreeBannerSectionGenerated
      {...buildGeneratedBannerProps<HomeThreeBannerSectionGeneratedProps>(
        "HomeThreeBannerSection",
        {
          divDiv0Div0Div0H10H1Renderer: createNodeChildrenRenderer(
            props.setting.Title,
          ),
          divDiv0Div0Div0P0PText: props.setting.Subtitle ?? undefined,
          divDiv0Div0Div1Link0LinkHref: props.setting.ButtonLink ?? undefined,
          divDiv0Div0Div1Link0Span0SpanText:
            props.setting.ButtonText ?? undefined,
          divDiv0Div0Div1Link0LinkVisible:
            props.setting.ButtonDisplayStatus.Key ===
            CenterOptions.Enum.Status.Active.key,
          divDiv0Div1Div0ImageWithBasePath0ImageSrc:
            getBannerImageUrl(props) ?? undefined,
        },
      )}
    />
  ),
  [BannerAppearance.Home4.key]: (props) => (
    <HomeFourBannerSectionGenerated
      {...buildGeneratedBannerProps<HomeFourBannerSectionGeneratedProps>(
        "HomeFourBannerSection",
        {
          sectionDiv0Div0Div0Div0Div1H10H1Renderer: createNodeChildrenRenderer(
            props.setting.Title,
          ),
          sectionDiv0Div0Div0Div0Div1P0PText:
            props.setting.Subtitle ?? undefined,
          sectionDiv0Div0Div0Div0Div0P0PText:
            props.setting.StatisticText1 ?? undefined,
          sectionDiv0Div0Div0Div0Div0P0Span0SpanText:
            props.setting.StatisticText2 ?? undefined,
          sectionDiv0Div0Div0Div0Div1Link0LinkHref:
            props.setting.ButtonLink ?? undefined,
          sectionDiv0Div0Div0Div0Div1Link0LinkText:
            props.setting.ButtonText ?? undefined,
          sectionDiv0Div0Div0Div0Div1Link0LinkVisible:
            props.setting.ButtonDisplayStatus.Key ===
            CenterOptions.Enum.Status.Active.key,
          sectionDiv0Div0Div1Div0ImageWithBasePath0ImageSrc:
            getBannerImageUrl(props) ?? undefined,
        },
      )}
    />
  ),
  [BannerAppearance.Home5.key]: (props) => (
    <HomeFiveBannerSectionGenerated
      {...applyHomeFiveSlideOverrides(
        buildGeneratedBannerProps<HomeFiveBannerSectionGeneratedProps>(
          "HomeFiveBannerSection",
          {
            divDiv0Div0Div0Div0Div0H10H1Renderer: createNodeChildrenRenderer(
              props.setting.Title,
            ),
            divDiv0Div0Div0Div0Div0Div0DivText:
              props.setting.Subtitle ?? undefined,
            divDiv0Div0Div0Div0Div0Div0Span0SpanText:
              props.setting.BadgeText ?? undefined,
            divDiv0Div0Div0Div0Link0LinkHref:
              props.setting.ButtonLink ?? undefined,
            divDiv0Div0Div0Div0Link0LinkText:
              props.setting.ButtonText ?? undefined,
            divDiv0Div0Div0Div0Link0LinkVisible:
              props.setting.ButtonDisplayStatus.Key ===
              CenterOptions.Enum.Status.Active.key,
            divDiv0Div0Div0Div0ImageWithBasePath0ImageSrc:
              getBannerImageUrl(props) ?? undefined,
            divDiv0Div0Div1Div0Div0H30H3Text:
              props.setting.StatisticText1 ?? undefined,
            divDiv0Div0Div1Div0Div0H30Span0SpanText:
              props.setting.StatisticText2 ?? undefined,
            bannerSectionBannerSlides: props.lists.Default.filter(
              (item) => item.Image?.Url,
            ).map((item) => ({
              key: item.Id,
              alt: item.Image.AltText,
              src: item.Image.Url!,
            })),
          },
        ),
        props,
      )}
    />
  ),
  [BannerAppearance.Home6.key]: (props) => (
    <HomeSixBannerSectionGenerated
      {...buildGeneratedBannerProps<HomeSixBannerSectionGeneratedProps>(
        "HomeSixBannerSection",
        {
          sectionDiv0Div0Div0Div0H10H1Renderer: createNodeChildrenRenderer(
            props.setting.Title,
          ),
          sectionDiv0Div0Div0Div0P0PText: props.setting.Subtitle ?? undefined,
          sectionDiv0Div0Div0Link0LinkHref:
            props.setting.ButtonLink ?? undefined,
          sectionDiv0Div0Div0Link0Span0SpanText:
            props.setting.ButtonText ?? undefined,
          sectionDiv0Div0Div0Link0LinkVisible:
            props.setting.ButtonDisplayStatus.Key ===
            CenterOptions.Enum.Status.Active.key,
          sectionDiv0Div0Div1Div0ImageWithBasePath0ImageSrc:
            getBannerImageUrl(props) ?? undefined,
        },
      )}
    />
  ),
  [BannerAppearance.Home7.key]: (props) => (
    <HomeSevenBannerSectionGenerated
      {...buildGeneratedBannerProps<HomeSevenBannerSectionGeneratedProps>(
        "HomeSevenBannerSection",
        {
          sectionDiv0Div0Div0Div0Div0H10H1Renderer: createNodeChildrenRenderer(
            props.setting.Title,
          ),
          sectionDiv0Div0Div0Div0Div0Div0P0PText:
            props.setting.Subtitle ?? undefined,
          sectionDiv0Div0Div0Div0Div0Div0Link0LinkHref:
            props.setting.ButtonLink ?? undefined,
          sectionDiv0Div0Div0Div0Div0Div0Link0LinkText:
            props.setting.ButtonText ?? undefined,
          sectionDiv0Div0Div0Div0Div0Div0Link0LinkVisible:
            props.setting.ButtonDisplayStatus.Key ===
            CenterOptions.Enum.Status.Active.key,
          sectionDiv0Div0Div0Div0Div1ImageWithBasePath0ImageSrc:
            getBannerImageUrl(props) ?? undefined,
        },
      )}
    />
  ),
  [BannerAppearance.Home8.key]: (props) => (
    <HomeEightBannerSectionGenerated
      {...buildGeneratedBannerProps<HomeEightBannerSectionGeneratedProps>(
        "HomeEightBannerSection",
        {
          divSection0Div0Div0Div0Div0Div0H10H1Renderer:
            createNodeChildrenRenderer(props.setting.Title),
          divSection0Div0Div0Div0Div0Div0Div0DivText:
            props.setting.Subtitle ?? undefined,
          divSection0Div0Div0Div0Div0Div1Link0LinkHref:
            props.setting.ButtonLink ?? undefined,
          divSection0Div0Div0Div0Div0Div1Link0LinkText:
            props.setting.ButtonText ?? undefined,
          divSection0Div0Div0Div0Div0Div1Link0LinkVisible:
            props.setting.ButtonDisplayStatus.Key ===
            CenterOptions.Enum.Status.Active.key,
          divSection0Div0Div0Div1Div0ImageWithBasePath0ImageSrc:
            getBannerImageUrl(props) ?? undefined,
        },
      )}
    />
  ),
  [BannerAppearance.Home9.key]: (props) => (
    <HomeNineBannerSectionGenerated
      {...buildGeneratedBannerProps<HomeNineBannerSectionGeneratedProps>(
        "HomeNineBannerSection",
        {
          divDiv0Div0Div0Div0Div0H10H1Renderer: createNodeChildrenRenderer(
            props.setting.Title,
          ),
          divDiv0Div0Div0Div0Div0P0PText: props.setting.Subtitle ?? undefined,
          divDiv0Div0Div0Div0Div0Div0Link0LinkHref:
            props.setting.ButtonLink ?? undefined,
          divDiv0Div0Div0Div0Div0Div0Link0LinkText:
            props.setting.ButtonText ?? undefined,
          divDiv0Div0Div0Div0Div0Div0Link0LinkVisible:
            props.setting.ButtonDisplayStatus.Key ===
            CenterOptions.Enum.Status.Active.key,
          divDiv0Div0Div0Div1Div0Div0ImageWithBasePath0ImageSrc:
            getBannerImageUrl(props) ?? undefined,
          divDiv0Div0Div0Div0Div0Div0Div0Div1Link0LinkText:
            props.pageData.CommonData.Tels?.[0] ?? undefined,
        },
      )}
    />
  ),
  [BannerAppearance.Home10.key]: (props) => (
    <HomeTenBannerSectionGenerated
      {...buildGeneratedBannerProps<HomeTenBannerSectionGeneratedProps>(
        "HomeTenBannerSection",
        {
          sectionDiv0Div0Div0Div0H10H1Renderer: createNodeChildrenRenderer(
            props.setting.Title,
          ),
          sectionDiv0Div0Div0Div0Div0Link0LinkHref:
            props.setting.ButtonLink ?? undefined,
          sectionDiv0Div0Div0Div0Div0Link0LinkText:
            props.setting.ButtonText ?? undefined,
          sectionDiv0Div0Div0Div0Div0Link0LinkVisible:
            props.setting.ButtonDisplayStatus.Key ===
            CenterOptions.Enum.Status.Active.key,
          sectionDiv0Div0Div0Div0Link0LinkHref:
            props.setting.ButtonLink ?? undefined,
          sectionDiv0Div0Div0Div0Link0LinkVisible:
            props.setting.ButtonDisplayStatus.Key ===
            CenterOptions.Enum.Status.Active.key,
          sectionDiv0Div0Div0Div0Link0Button0ButtonDataText:
            buildAnimatedButtonText(props.setting.ButtonText),
          sectionDiv0Div0Div1Div0ImageWithBasePath0ImageSrc:
            getBannerImageUrl(props) ?? undefined,
        },
      )}
    />
  ),
  [BannerAppearance.Home12.key]: (props) => (
    <HomeTwelveBannerSectionGenerated
      {...buildGeneratedBannerProps<HomeTwelveBannerSectionGeneratedProps>(
        "HomeTwelveBannerSection",
        {
          sectionDiv0Div0Div0Div0Div0H10H1Renderer: createNodeChildrenRenderer(
            props.setting.Title,
          ),
          sectionDiv0Div0Div0Div0Div0P0PText:
            props.setting.Subtitle ?? undefined,
          sectionDiv0Div0Div0Div0Div0Div1Input0InputPlaceholder:
            props.setting.SearchPlaceholder ?? undefined,
          sectionDiv0Div0Div0Div0Div0Div1Button0ButtonText:
            props.setting.ButtonText ?? undefined,
          sectionDiv0Div0Div0Div0Div0Div1Button0ButtonVisible:
            props.setting.SearchDisplayStatus.Key ===
            CenterOptions.Enum.Status.Active.key,
          sectionDiv0Div0Div1Div0ImageWithBasePath0ImageSrc:
            getBannerImageUrl(props) ?? undefined,
          sectionDiv0Div0Div1Div0P0PText:
            props.setting.StatisticText1 ?? undefined,
          sectionDiv0Div0Div1Div0P1PText:
            props.setting.StatisticText2 ?? undefined,
          sectionDiv0Div0Div1Div0P2PText:
            props.setting.StatisticText3 ?? undefined,
          sectionDiv0Div0Div1Div0P3PText:
            props.setting.StatisticText4 ?? undefined,
        },
      )}
    />
  ),
  [BannerAppearance.Home13.key]: (props) => (
    <HomeThirteenBannerSectionGenerated
      {...applyBannerListItemImages(
        buildGeneratedBannerProps<HomeThirteenBannerSectionGeneratedProps>(
          "HomeThirteenBannerSection",
          {
            sectionDiv0Div0Div0Div0H10H1Renderer: createNodeChildrenRenderer(
              props.setting.Title,
            ),
            sectionDiv0Div0Div0Div0P0PText: props.setting.Subtitle ?? undefined,
            sectionDiv0Div0Div0Div0Div0Link0LinkHref:
              props.setting.ButtonLink ?? undefined,
            sectionDiv0Div0Div0Div0Div0Link0LinkText:
              props.setting.ButtonText ?? undefined,
            sectionDiv0Div0Div0Div0Div0Link0LinkVisible:
              props.setting.ButtonDisplayStatus.Key ===
              CenterOptions.Enum.Status.Active.key,
            sectionDiv0Div0Div1Div0ImageWithBasePath0ImageSrc:
              getBannerImageUrl(props) ?? undefined,
            sectionDiv0H20H2Text: props.setting.BadgeText ?? undefined,
          },
        ),
        props,
      )}
    />
  ),
  [BannerAppearance.Home14.key]: (props) => (
    <HomeFourteenBannerSectionGenerated
      {...applyHomeFourteenSlideOverrides(
        buildGeneratedBannerProps<HomeFourteenBannerSectionGeneratedProps>(
          "HomeFourteenBannerSection",
          {
            bannerSectionBannerSlidesH1Renderer: (_item, defaultNode) =>
              cloneNodeWithChildren(
                defaultNode,
                renderHtmlValue(props.setting.Title),
              ),
          },
        ),
        props,
      )}
    />
  ),
};

const GeneratedBannerSection: FC<BannerSectionProps> = (props) => {
  return renderGeneratedSection(
    props,
    generatedBannerRenderers,
    BannerAppearance.Home1.key,
    (x) => x.setting.Appearance?.Key,
  );
};

export default GeneratedBannerSection;
